import React, { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Login.css";
import "./SignUp.css";
import LoginLogo from "../../Components/Assets/images/logo2.png";
import { ChevronLeft, Eye, EyeOff } from "lucide-react";
import { useTitle } from "../../hooks/useTitle";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const validatePassword = (pw) =>
  /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(pw);

const WORKER = "https://email-api.trendbayds.com";

// ── Confirm new password (token + userId in URL) ─────────────────────────────

function PasswordResetForm({ token, userId }) {
  useTitle("Set New Password - TrendBay Seller");
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: "center" }}>
          <a href="/"><img className="auth-logo" src={LoginLogo} alt="TrendBay" /></a>
          <div className="auth-badge">🏪 Seller Portal</div>
          <h1 className="auth-heading">Password reset!</h1>
          <p className="auth-sub">Your password has been updated.</p>
          <Link to="/login" className="auth-btn-primary" style={{ display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", marginTop: 8 }}>
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (!validatePassword(newPassword)) {
      setError("Password must have at least 8 characters, 1 uppercase letter, 1 number & 1 symbol.");
      return;
    }
    if (!WORKER) { setError("Service not configured. Please try again later."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${WORKER}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, userId, newPassword }),
      });
      const data = await res.json();
      if (!data.ok) {
        setError(data.error || "Failed to reset password. The link may have expired.");
        return;
      }
      setDone(true);
    } catch {
      setError("Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <a href="/"><img className="auth-logo" src={LoginLogo} alt="TrendBay" /></a>
        <div className="auth-badge">🏪 Seller Portal</div>
        <h1 className="auth-heading">Create a New Password</h1>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label className="auth-label">New Password</label>
            <div className="auth-pw-wrap">
              <input
                className="auth-input"
                type={showPw ? "text" : "password"}
                placeholder="Min. 8 chars, 1 uppercase, 1 number, 1 symbol"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                required
              />
              <span className="auth-pw-toggle" onClick={() => setShowPw((p) => !p)}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </span>
            </div>
          </div>
          <div className="auth-field">
            <label className="auth-label">Confirm Password</label>
            <div className="auth-pw-wrap">
              <input
                className="auth-input"
                type={showConfirm ? "text" : "password"}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                required
              />
              <span className="auth-pw-toggle" onClick={() => setShowConfirm((p) => !p)}>
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </span>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="signup-pw-mismatch">Passwords don't match</p>
            )}
          </div>
          <button type="submit" className="auth-btn-primary" disabled={loading}>
            {loading ? "Resetting…" : "Reset Password"}
          </button>
        </form>

        <Link to="/login" className="auth-forgot" style={{ display: "block", marginTop: 12, textAlign: "center" }}>
          <ChevronLeft size={13} style={{ verticalAlign: "middle" }} /> Back to Sign In
        </Link>
      </div>
    </div>
  );
}

// ── Request reset (no token in URL) ─────────────────────────────────────────

function RequestResetForm() {
  useTitle("Reset Password - TrendBay Seller");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!WORKER) { setError("Service not configured. Please try again later."); return; }
    setLoading(true);
    setError("");
    try {
      await fetch(`${WORKER}/initiate-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), appType: "seller" }),
      });
      setSent(true);
    } catch {
      setSent(true); // Always show success to prevent enumeration
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: "center" }}>
          <a href="/"><img className="auth-logo" src={LoginLogo} alt="TrendBay" /></a>
          <div className="auth-badge">🏪 Seller Portal</div>
          <h1 className="auth-heading">Check your inbox</h1>
          <p className="auth-sub" style={{ maxWidth: 300, margin: "0 auto 20px" }}>
            If an account with <strong>{email}</strong> exists, we sent a reset link. Check your spam folder too.
          </p>
          <Link to="/login" className="auth-btn-outline" style={{ display: "block", textDecoration: "none", textAlign: "center" }}>
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <a href="/"><img className="auth-logo" src={LoginLogo} alt="TrendBay" /></a>
        <div className="auth-badge">🏪 Seller Portal</div>
        <h1 className="auth-heading">Reset Your Password</h1>
        <p className="auth-sub">Enter your seller account email and we'll send a reset link.</p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label className="auth-label">Email address</label>
            <input
              className="auth-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              required
            />
          </div>
          <button type="submit" className="auth-btn-primary" disabled={loading}>
            {loading ? "Sending…" : "Send Reset Link"}
          </button>
        </form>

        <Link to="/login" className="auth-forgot" style={{ display: "block", marginTop: 12, textAlign: "center" }}>
          <ChevronLeft size={13} style={{ verticalAlign: "middle" }} /> Back to Sign In
        </Link>
      </div>
    </div>
  );
}

// ── Router ────────────────────────────────────────────────────────────────────

const ResetPassword = () => {
  const query = useQuery();
  const token = query.get("token");
  const userId = query.get("userId");

  if (token && userId) {
    return <PasswordResetForm token={token} userId={userId} />;
  }
  return <RequestResetForm />;
};

export default ResetPassword;
